import classNames from "classnames";
import {
  TournamentScheduleMatch,
  TournamentScheduleTeam,
} from "~/shared/helpers/tournaments/mapBracketsToTournamentSchedule";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { generateImageUrl } from "@portal-frontend-ssr/blast-assets-api";
import { GameId } from "@portal-frontend-ssr/providers";
import { AdaptiveLink } from "@portal-frontend-ssr/ui";
import { ClientOnly } from "remix-utils/client-only";
import { useMemo } from "react";

const Team = ({
  teamType,
  team,
  hideImage,
}: {
  teamType: "teamA" | "teamB";
  team: TournamentScheduleTeam | null;
  hideImage: boolean;
}) => {
  return (
    <>
      <div
        className={classNames("flex w-full flex-1 items-center justify-end gap-2", {
          "flex-row-reverse": teamType === "teamB",
        })}
      >
        {team ? (
          <>
            <div
              className={classNames("truncate font-style-body-b4 @3xl/row:font-style-body-b3", {
                "text-right": teamType === "teamA",
                "text-left": teamType === "teamB",
              })}
            >
              {team?.name ?? "???"}
            </div>

            {!hideImage && (
              <img
                src={generateImageUrl("teams", team.uuid, {
                  format: "auto",
                  width: "20px",
                })}
                className="col-span-1 size-5 object-contain"
                alt={team?.name ?? ""}
              />
            )}
          </>
        ) : (
          <div
            className={classNames("flex items-center gap-2 @3xl/row:gap-3", {
              "flex-row-reverse": teamType === "teamB",
            })}
          >
            <span className="font-style-body-b3">TBD</span>
            <div className="flex size-5 items-center justify-center rounded-full bg-background-80 text-foreground-90 font-style-body-b3">
              ?
            </div>
          </div>
        )}
      </div>
    </>
  );
};

type MatchStatus = "scheduled" | "live" | "completed";

const MatchRow = ({
  match,
  tournamentName,
  hideImages = false,
  gameId = "cs",
}: {
  match: TournamentScheduleMatch;
  tournamentName: string;
  hideImages?: boolean;
  gameId?: GameId;
}) => {
  const { teamA, teamB } = match;

  const rowLink = useMemo(() => {
    if (match.isLive) {
      if (gameId === "cs") {
        return { linkTo: `/live`, isExternal: false };
      } else if (gameId === "dota") {
        if (match.metadata?._t === "dota_match" && match.metadata?.externalStreamUrl) {
          return { linkTo: match.metadata?.externalStreamUrl, isExternal: true };
        }
        return { linkTo: `/dota/live`, isExternal: false };
      } else if (gameId === "rl") {
        if (match.metadata?._t === "rl_match" && match.metadata?.externalStreamUrl) {
          return { linkTo: match.metadata?.externalStreamUrl, isExternal: true };
        }
      }
    }

    return {
      linkTo: generateMatchPageUrl({
        game: gameId,
        matchUuid: match.uuid,
        matchName: match.label,
        tournamentName,
        teamAName: teamA?.shorthand,
        teamBName: teamB?.shorthand,
      }),
      isExternal: false,
    };
  }, [gameId, match, tournamentName, teamA, teamB]);

  const matchStatus: MatchStatus = match.isCompleted ? "completed" : match.isLive ? "live" : "scheduled";
  const teamAScore = match.teamA?.score;
  const teamBScore = match.teamB?.score;
  const hasScores = teamAScore !== undefined && teamBScore !== undefined;

  const renderedMatchRow = (
    <>
      {/* card title top - only shown on mobile */}
      <time className={"min-h-6 @3xl/row:hidden"}>
        <ClientOnly
          fallback={
            <div className="flex animate-pulse items-center gap-1 text-neutral">
              <div className="h-3 w-8 rounded-full bg-background-85" />
              {" - "}
              <div className="h-3 w-20 rounded-full bg-background-85" />
            </div>
          }
        >
          {() => (
            <div className="flex flex-row justify-between gap-1 px-1">
              <span className="text-neutral font-style-body-b3">
                {match.time} - {match.label} - {match.stage}
              </span>
              <div className="flex flex-row items-center gap-1">
                {matchStatus === "live" && <span className="text-error font-style-label-l4">Live</span>}
                <span className="text-neutral font-style-body-b3">{match.type.toLocaleUpperCase()}</span>
              </div>
            </div>
          )}
        </ClientOnly>
      </time>

      {/* card and grid */}
      <div
        className={classNames(
          "relative flex items-center justify-items-center gap-2 rounded-small px-4 py-1.5 transition-colors",
        )}
      >
        {/* match details left - hidden on mobile */}
        <div className={"absolute left-4 top-1/2 hidden w-full -translate-y-1/2 items-center @3xl/row:flex"}>
          <ClientOnly
            fallback={
              <div className="flex w-20">
                <div className="h-3 w-9 rounded-full bg-background-85" />
              </div>
            }
          >
            {() => <time className="w-20 font-style-label-l3">{match.time}</time>}
          </ClientOnly>
          <div className="flex flex-col">
            <span className="font-style-label-l4">{match.label}</span>
            <span className="text-neutral font-style-body-b3">{match.stage}</span>
          </div>
        </div>

        {/* team A */}
        <Team hideImage={hideImages} teamType={"teamA"} team={teamA} />

        {/* score */}
        <div className="flex shrink-0 items-center gap-2 rounded-small bg-background-100 px-3 py-1 @3xl/row:px-4">
          <span className="w-9 text-center font-style-label-l3">
            {hasScores && matchStatus !== "scheduled" ? `${teamAScore} : ${teamBScore}` : "VS"}
          </span>
        </div>

        {/* team B */}
        <Team hideImage={hideImages} teamType={"teamB"} team={teamB} />
        <div className="absolute right-4 hidden flex-row items-center gap-2 @3xl/row:flex">
          {matchStatus === "live" && <span className="text-error font-style-label-l4">Live</span>}
          <span className="text-neutral font-style-body-b4">{match.type.toLocaleUpperCase()}</span>
        </div>
      </div>
    </>
  );

  return rowLink && rowLink.linkTo ? (
    <AdaptiveLink
      to={rowLink.linkTo}
      isExternal={rowLink.isExternal}
      className={"bg-background-95 text-white no-underline @container/row hover:bg-background-90"}
    >
      {renderedMatchRow}
    </AdaptiveLink>
  ) : (
    <div className="bg-background-95 @container/row">{renderedMatchRow}</div>
  );
};
export { MatchRow };
